import React from 'react'
import { Redirect } from 'react-router-dom'
import Header from '../containers/HeaderContainer'

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

function sortNewestFirst(items) {
    return (items || []).slice().sort((a, b) => {
        return new Date(b.lastModified || 0).getTime() - new Date(a.lastModified || 0).getTime()
    })
}

class AssetLibrary extends React.Component {
    state = {
        checkingUser: true,
        items: [],
        loading: false,
        uploading: false,
        notice: ''
    }

    componentDidMount() {
        if (!this.props.user) {
            this.props.checkUserSession(() => {
                this.setState({ checkingUser: false }, () => this._load())
            })
        } else {
            this.setState({ checkingUser: false }, () => this._load())
        }
    }

    _load() {
        this.setState({ loading: true, notice: '' }, () => {
            this.props.listAssets('background', (response) => {
                if (!response || response.error) {
                    this.setState({ loading: false, items: [], notice: (response && response.message) || 'Unable to load the asset library.' })
                    return
                }
                this.setState({ loading: false, items: sortNewestFirst(response.items) })
            })
        })
    }

    async _upload(event) {
        const input = event.target
        const file = input && input.files && input.files[0]
        if (!file) return
        this.setState({ uploading: true, notice: '' })
        try {
            const dataUrl = await readFileAsDataUrl(file)
            this.props.uploadAsset({
                assetType: 'background',
                fileName: file.name,
                dataUrl
            }, (response) => {
                if (!response || response.error || !response.asset) {
                    this.setState({ uploading: false, notice: (response && response.message) || 'Unable to upload asset.' })
                    return
                }
                this.setState((prevState) => ({
                    uploading: false,
                    notice: 'Asset uploaded to the shared library.',
                    items: sortNewestFirst([response.asset, ...prevState.items.filter((item) => item.url !== response.asset.url)])
                }))
            })
        } catch (error) {
            this.setState({ uploading: false, notice: 'Unable to read that file.' })
        }
        if (input) input.value = ''
    }

    render() {
        if (this.state.checkingUser || (this.state.loading && !this.props.user)) {
            return <div className="px-4 py-5 text-center">Loading asset library...</div>
        }

        if (!this.props.user) {
            return <Redirect to='/' />
        }

        return (
            <div className="min-h-screen lg:flex">
                <Header />
                <main className="min-w-0 flex-1 px-4 py-4 sm:px-6 lg:px-10 lg:py-8">
                    <div className="mx-auto max-w-7xl">
                        <div className="rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                <div className="max-w-3xl">
                                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">Asset library</h1>
                                    <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                                        Upload shared background images once, then reuse them across client branding screens.
                                    </p>
                                </div>
                                <label className="inline-flex cursor-pointer items-center justify-center rounded-2xl bg-[#1cd997] px-5 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-95">
                                    {this.state.uploading ? 'Uploading...' : 'Upload background'}
                                    <input type="file" accept="image/*" className="hidden" onChange={(event) => this._upload(event)} />
                                </label>
                            </div>
                            {this.state.notice ? <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200">{this.state.notice}</div> : null}
                        </div>

                        <section className="mt-6 rounded-[28px] border border-slate-200/70 bg-white/80 p-6 shadow-soft backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Background assets</h2>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{this.state.loading ? 'Loading assets...' : ((this.state.items || []).length + ' shared assets available')}</p>
                                </div>
                            </div>

                            {!this.state.loading && this.state.items && this.state.items.length > 0 ?
                                <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                    {this.state.items.map((item) => (
                                        <div key={item.key || item.url} className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                                            <div className="h-64 w-full bg-slate-100 dark:bg-slate-950">
                                                <div className="h-full w-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(' + item.url + ')' }} />
                                            </div>
                                            <div className="space-y-1 px-4 py-4">
                                                <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.name || 'Shared asset'}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">{item.lastModified ? new Date(item.lastModified).toLocaleString() : 'Shared asset'}</div>
                                                <div className="truncate text-xs text-sky-600 dark:text-sky-300">{item.url}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                : null}

                            {!this.state.loading && (!this.state.items || this.state.items.length === 0) ?
                                <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">No shared background assets yet.</div>
                                : null}
                        </section>
                    </div>
                </main>
            </div>
        )
    }
}

export default AssetLibrary
